import React from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { usePublicSettings } from "../lib/publicSettings";

const waLink = (raw) => {
  if (!raw) return null;
  const digits = raw.replace(/[^0-9+]/g, "").replace(/^\+/, "");
  return digits ? `https://wa.me/${digits}` : null;
};

export default function Contact() {
  const settings = usePublicSettings();
  const whatsapp = waLink(settings?.whatsapp_number);
  const address = (settings?.contact_address || "").trim();
  const mapSrc = address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&hl=it&z=15&output=embed`
    : null;

  const cards = [
    { icon: Phone, label: "Telefono", value: settings?.contact_phone, href: settings?.contact_phone ? `tel:${settings.contact_phone.replace(/\s+/g, "")}` : null },
    { icon: Mail, label: "Email", value: settings?.contact_email, href: settings?.contact_email ? `mailto:${settings.contact_email}` : null },
    { icon: MapPin, label: "Sede", value: settings?.contact_address },
    { icon: Clock, label: "Orari", value: (settings?.opening_hours || "").trim() },
  ];
  if (whatsapp) {
    cards.splice(2, 0, {
      icon: MessageCircle,
      label: "WhatsApp",
      value: settings?.whatsapp_number,
      href: whatsapp,
      accent: "#25D366",
    });
  }

  return (
    <div className="App min-h-screen flex flex-col" data-testid="contact-page">
      <Header />
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16 md:py-24 w-full">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="divider-gold" />
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Contatti</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-bold mb-8 leading-tight heading-silver-shine" data-testid="contact-hero-title">
          Parla direttamente con <span className="italic">Rocco</span>
        </h1>
        <p className="text-white/85 text-lg max-w-2xl leading-relaxed mb-16">
          È a tua disposizione per consigli, prove su strada e trattative personalizzate.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {cards.map((c) => {
            const Wrap = c.href ? "a" : "div";
            return (
              <Wrap
                key={c.label}
                {...(c.href ? { href: c.href, target: c.href.startsWith("http") ? "_blank" : undefined, rel: c.href.startsWith("http") ? "noopener noreferrer" : undefined } : {})}
                className="card-sharp p-8 group block"
                data-testid={`contact-${c.label.toLowerCase()}`}
              >
                <c.icon size={22} style={c.accent ? { color: c.accent } : undefined} className={c.accent ? "mb-4" : "text-gold mb-4"} />
                <p className="text-sm uppercase tracking-widest text-midnight font-bold mb-2">{c.label}</p>
                {c.value ? (
                  <p className="font-display text-2xl text-midnight font-semibold group-hover:text-gold transition-colors whitespace-pre-line break-words">
                    {c.value}
                  </p>
                ) : (
                  <p className="text-midnight/50 italic text-base">Non ancora inserito</p>
                )}
              </Wrap>
            );
          })}
        </div>

        {mapSrc && (
          <div className="mt-16" data-testid="contact-map">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Dove siamo</span>
            </div>
            <div className="card-sharp overflow-hidden">
              <iframe
                title="Mappa sede Guzzardella Auto"
                src={mapSrc}
                width="100%"
                height="420"
                style={{ border: 0, display: "block", filter: "grayscale(0.15) contrast(1.02)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-widest text-midnight/60 hover:text-gold transition-colors"
              data-testid="contact-map-open-btn"
            >
              <MapPin size={13} /> Apri in Google Maps
            </a>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
